import { useAdministrationContext } from '@openx/components-administration/lib/utils/context/AdministrationContext';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { NameCell } from '@openx/components-core/lib/NameCell/NameCell';
import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder';
import type { InventoryListByIdAPI } from '@openx/types/inventoryList';
import type { TableColumn } from '@openx/types/tableHelpers';
import { toUTCDate } from '@openx/utils/lib/date';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type UseGetListTableColumnsProps = (searchPhrase: string) => TableColumn<InventoryListByIdAPI>[];

export const useGetListTableColumns: UseGetListTableColumnsProps = searchPhrase => {
  const { t } = useTranslation();
  const { useAccountOptionsFetch } = useAdministrationContext();

  const {
    accounts: { loading, mapById },
  } = useAccountOptionsFetch();

  return useMemo(
    () => [
      {
        key: 'name',
        title: t('Name'),
        render: ({ name, id }) => (
          <NameCell name={name} pathTo={`/inventory-lists/${id}/details`} searchPhrase={searchPhrase} />
        ),
        width: '30%',
        disabled: true,
      },
      {
        key: 'account_uid',
        render: ({ account_uid }) => (loading ? <Loader /> : (mapById[account_uid]?.name ?? account_uid)),
        title: t('Organization'),
        width: '15%',
      },
      {
        key: 'type_full',
        title: t('List Type'),
        render: ({ type_full, values }) =>
          type_full === 'inventorylist.bundle'
            ? t('Bundles: {count}', { count: values.length })
            : t('Domains: {count}', { count: values.length }),
        width: '15%',
      },

      {
        key: 'modified_date',
        title: t('Last Modified'),
        render: ({ modified_date }) => (modified_date ? toUTCDate(modified_date) : <ValuePlaceholder />),
        width: '15%',
      },
      {
        key: 'created_date',
        title: t('Created'),
        render: ({ created_date }) => (created_date ? toUTCDate(created_date) : <ValuePlaceholder />),
        width: '15%',
      },
    ],
    // biome-ignore lint: searchPhrase is a primitive and safe to use here
    [loading, mapById, t, searchPhrase]
  );
};
